/**
 * How to pay (GH-240, docs/ux-flows.md).
 *
 * Opens only after the donor has committed to an amount, so the amount screen stays uncluttered
 * and this screen asks exactly one question. Every way of paying is a box the donor taps once,
 * and each box goes *directly* to its own thing (owner-directed 2026-08-17):
 *
 *   - **Apple Pay / Google Pay** are Stripe's real wallet buttons (Express Checkout Element) —
 *     tapping one opens the wallet sheet itself, never a card form.
 *   - **Card** opens the card-only form (the Payment Element with wallets off).
 *   - **Bank** re-quotes at the ACH rate and opens Plaid Link (or Stripe's bank flow on the
 *     backup rail).
 *   - **Pledge** records a promise; nothing is processed.
 *
 * Three rules govern what appears here:
 *
 *   1. **The server says what is permitted; the device says what it can present.** Both must be
 *      true. Stripe's wallet element renders nothing on hardware without a wallet, and the
 *      fallback wallet buttons are hidden on hardware that cannot present them.
 *   2. **No way of paying appears twice.** The card screen no longer re-offers the wallets;
 *      each box is the only door to its method.
 *   3. **A simulated rail says so, on screen.** A payment button that quietly moves nothing is
 *      the worst failure available to this product, so the banner is not dismissible.
 *
 * There used to be a second path through this sheet, for saying what you would give to an
 * organization that has not claimed its listing. It is archived (GH-794): an unclaimed
 * organization never reaches a payment surface at all.
 */
import {
  formatMoney,
  formatMoneyCompact,
  type MoneyWire,
  type PaymentMethodId,
  type PaymentMethodOption,
  type SavedInstrument,
} from "@givehere/contracts";
import { applePayAvailable, googlePayAvailable } from "./wallets.js";
import { useDialogFocus } from "@givehere/ui";
import { WalletExpressBoxes, type WalletMethod } from "./WalletExpress.js";
import { Explain } from "./Explain.js";

export type SheetChoice = Exclude<PaymentMethodId, "check">;

/**
 * Whether this device can present the method at all, independent of whether it is permitted.
 *
 * Card and pledge are always presentable; the wallets are not.
 */
export function devicePresentable(id: PaymentMethodId): boolean {
  if (id === "apple_pay") return applePayAvailable();
  if (id === "google_pay") return googlePayAvailable();
  return true;
}

/** "Visa ····4242" / "Chase ····6789" — how a saved instrument introduces itself. */
export function instrumentLabel(instrument: SavedInstrument): string {
  if (instrument.kind === "us_bank_account") {
    return `${instrument.bankName ?? "Bank"} ····${instrument.last4}`;
  }
  const brand = instrument.brand
    ? instrument.brand.charAt(0).toUpperCase() + instrument.brand.slice(1)
    : "Card";
  return `${brand} ····${instrument.last4}`;
}

/** Everything the live wallet buttons need; absent on the synthetic rail. */
export type WalletExpressProps = {
  amountMinor: string;
  currency: string;
  createIntent: (method: WalletMethod) => Promise<{ clientSecret: string | null }>;
  onAuthorized: () => void;
  /** The wallet's authorization failed after the server answered: the next press is new. */
  onFailed?: () => void;
};

/** Every sentence this sheet says about the gift, in one place. */
const SHEET_COPY = {
  /** Under the amount. A fact about to become a charge. */
  subject: (organization: string) => `in support of ${organization}`,
  pledgeName: "Pledge",
  /** True here: a pledge against a live event is a debt the donor means to settle. */
  pledgeHint: (amount: string) => `${amount} — pay later`,
  foot: "No account needed",
  footDetail: "No account needed. Every dollar goes to charitable purpose.",
} as const;

export function PaymentSheet({
  total,
  pledgeAmount,
  organizationDisplayName,
  methods,
  savedInstruments,
  busy,
  error,
  walletExpress,
  onChoose,
  onPaySaved,
  onDismiss,
}: {
  total: MoneyWire;
  /** The donation alone. A pledge carries no cost support, because nothing is processed. */
  pledgeAmount: MoneyWire;
  organizationDisplayName: string;
  methods: readonly PaymentMethodOption[];
  /** The signed-in donor's saved instruments (GH-330); empty for guests. */
  savedInstruments: readonly SavedInstrument[];
  busy: boolean;
  error: string | null;
  /** Present when the real rail can put actual wallet buttons on this sheet. */
  walletExpress: WalletExpressProps | null;
  onChoose: (choice: SheetChoice) => void;
  /** One tap on a saved instrument: charge it and go straight to the receipt. */
  onPaySaved: (instrument: SavedInstrument) => void;
  onDismiss: () => void;
}) {
  const panelRef = useDialogFocus<HTMLDivElement>();
  const copy = SHEET_COPY;
  /** Permitted here, and presentable on this device, in the server's offer order. */
  const offered = methods
    .filter((method) => method.available && devicePresentable(method.id))
    .map((method) => method.id)
    .filter((id): id is SheetChoice => id !== "check");

  const simulated = methods.some((method) => method.available && method.mode === "simulated");
  const check = methods.find((method) => method.id === "check");

  const walletPermitted = offered.some((id) => id === "apple_pay" || id === "google_pay");
  // The element owns the wallets whenever it is mounted; the plain buttons exist only for the
  // simulated rail, where there is no Stripe to draw the real ones.
  const fallbackWallets = walletExpress
    ? []
    : offered.filter((id) => id === "apple_pay" || id === "google_pay");
  const boxes = offered.filter(
    (id) => id !== "apple_pay" && id !== "google_pay" && id !== "pledge",
  );
  const pledgeOffered = offered.includes("pledge");

  // A saved instrument appears only when its underlying method is permitted right now — a
  // saved bank account is hidden when the bank rail is off, exactly as the Bank box would be.
  const reusable = savedInstruments.filter((instrument) =>
    instrument.kind === "us_bank_account" ? offered.includes("bank") : offered.includes("card"),
  );

  const boxLabel = (id: SheetChoice) => {
    if (id === "bank")
      return (
        <>
          <span className="method-box__name">Bank</span>
          <span className="method-box__hint">ACH — lowest cost</span>
        </>
      );
    return (
      <>
        <span className="method-box__name">Card</span>
        <span className="method-box__hint">Credit or debit</span>
      </>
    );
  };

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="sheet-title">
      <div className="sheet__panel lattice-edge" ref={panelRef} tabIndex={-1}>
        <button type="button" className="sheet__close" onClick={onDismiss} aria-label="Go back">
          &#x2715;
        </button>

        {/* The full total, with cents: a fact about to become a charge. */}
        <p className="sheet__amount" id="sheet-title">
          {formatMoney(total)}
        </p>
        <p className="sheet__to">{copy.subject(organizationDisplayName)}</p>

        {simulated && (
          <Explain
            className="sheet__testmode"
            label="Test mode — nothing moves"
            title="What test mode means"
            tone="loud"
          >
            {/* The banner stays on screen and stays non-dismissible: a payment button that quietly
                moves nothing is the worst failure available to this product, and five words is
                enough to say so. The detail is what "nothing moves" means concretely. */}
            <p>No money moves and no bank is involved.</p>
          </Explain>
        )}

        {error && (
          <p className="notice notice--error" role="alert">
            {error}
          </p>
        )}

        {reusable.length > 0 && (
          <div className="sheet__saved" aria-label="Saved payment methods">
            {reusable.map((instrument) => (
              <button
                key={instrument.id}
                type="button"
                className="method-box method-box--saved"
                disabled={busy}
                onClick={() => onPaySaved(instrument)}
              >
                <span className="method-box__name">{instrumentLabel(instrument)}</span>
                <span className="method-box__hint">Pay {formatMoney(total)} in one tap</span>
              </button>
            ))}
          </div>
        )}

        {/*
          Wrapped so the *decision* is observable, not just its output.

          `WalletExpressBoxes` mounts Stripe's element, which renders nothing under jsdom — so a
          test asserting "no wallet button appears" passes whether or not `walletPermitted` guards
          this branch, and mutation testing found exactly that: deleting the guard passed the whole
          web suite. The wrapper always renders when the branch is taken, so its presence is the
          thing a test can hold, and its absence means the guard really refused.
        */}
        {walletExpress && walletPermitted && (
          <div className="sheet__wallet-express" data-wallet-express="">
            <WalletExpressBoxes
              amountMinor={walletExpress.amountMinor}
              currency={walletExpress.currency}
              createIntent={walletExpress.createIntent}
              onAuthorized={walletExpress.onAuthorized}
              {...(walletExpress.onFailed ? { onFailed: walletExpress.onFailed } : {})}
            />
          </div>
        )}

        <div className="sheet__methods">
          {fallbackWallets.map((id) => (
            <button
              key={id}
              type="button"
              className={id === "apple_pay" ? "wallet wallet--apple" : "wallet wallet--google"}
              disabled={busy}
              onClick={() => onChoose(id)}
            >
              {id === "apple_pay" ? (
                <>
                  <span aria-hidden="true"></span> Pay
                </>
              ) : (
                <>
                  <span className="wallet__g" aria-hidden="true">
                    G
                  </span>{" "}
                  Pay
                </>
              )}
            </button>
          ))}

          <div className="sheet__boxes">
            {boxes.map((id) => (
              <button
                key={id}
                type="button"
                className="method-box"
                disabled={busy}
                onClick={() => onChoose(id)}
              >
                {boxLabel(id)}
              </button>
            ))}
            {pledgeOffered && (
              <button
                type="button"
                className="method-box method-box--quiet"
                disabled={busy}
                onClick={() => onChoose("pledge")}
              >
                <span className="method-box__name">{copy.pledgeName}</span>
                <span className="method-box__hint">
                  {copy.pledgeHint(formatMoneyCompact(pledgeAmount))}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Check is listed rather than hidden so a donor holding one is told what to do with it.
            There is no upload path in this codebase to enable: check images are prohibited data
            (SPEC.md §16.5, DEC-013), so this line will not become a button by flipping a flag. */}
        {check && !check.available && (
          <p className="sheet__unavailable">Check — {check.unavailableReason}</p>
        )}

        {/*
           DEC-028: this used to read "Your gift goes directly to the organization", which is the
           sentence counsel named as the Lane B risk — it promises delivery and asserts a direct
           donor-to-organization relationship, contradicting the Foundation-as-donee position the
           receipt itself takes. The reassurance worth keeping is that no account is needed and
           nothing is skimmed; neither of those requires claiming where the money lands.
        */}
        <Explain className="sheet__foot" label={copy.foot} title="Giving here">
          <p>{copy.footDetail}</p>
        </Explain>
      </div>
    </div>
  );
}
